import { useEffect, useState } from "react";
import { approveUser, deactivateUser, getCurrentUser, getPendingUsers, getUsers, updateUser } from "../api/users";
import "./AdminUsers.css";

const PAGE_SIZE = 100;

function unwrapProfile(data) {
  return data?.user || data?.profile || data;
}

function unwrapUsers(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.pendingUsers)) return data.pendingUsers;
  if (Array.isArray(data?.pending_users)) return data.pending_users;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.users)) return data.data.users;
  if (Array.isArray(data?.data?.pendingUsers)) return data.data.pendingUsers;
  return [];
}

function userId(user) {
  return user.id ?? user.userId ?? user.user_id;
}

function sameUser(first, second) {
  const firstId = userId(first);
  const secondId = userId(second);
  return firstId != null && secondId != null && String(firstId) === String(secondId);
}

function userIsPending(user) {
  const status = String(user.status || "").toLowerCase();
  return status === "pending" || user.is_approved === false || user.isApproved === false;
}

function userIsInactive(user) {
  if (userIsPending(user)) return false;
  const status = String(user.status || "").toLowerCase();
  if (["inactive", "deactivated"].includes(status)) return true;
  if (typeof user.is_active === "boolean") return !user.is_active;
  if (typeof user.isActive === "boolean") return !user.isActive;
  return false;
}

function userStatus(user) {
  if (typeof user.status === "string" && user.status) {
    return user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase();
  }
  if (userIsPending(user)) return "Pending";
  if (userIsInactive(user)) return "Inactive";
  if (user.is_approved === true || user.isApproved === true) return "Approved";
  return "Active";
}

export default function AdminUsers({ token }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ username: "", first_name: "", last_name: "" });
  const [savingId, setSavingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let isActive = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const profileResponse = await getCurrentUser(token);
        const profile = unwrapProfile(profileResponse.data);
        if (!isActive) return;
        setCurrentUser(profile);
        if (profile?.is_admin !== true) return;

        const [usersResponse, pendingResponse] = await Promise.all([
          getUsers(token, PAGE_SIZE, 0),
          getPendingUsers(token),
        ]);
        const page = unwrapUsers(usersResponse.data);
        const pending = unwrapUsers(pendingResponse.data).map((user) => ({
          ...user,
          status: "pending",
          is_approved: false,
          is_active: false,
        }));
        const mergedUsers = [...page];
        pending.forEach((pendingUser) => {
          const existingIndex = mergedUsers.findIndex((user) => sameUser(user, pendingUser));
          if (existingIndex === -1) mergedUsers.push(pendingUser);
          else mergedUsers[existingIndex] = { ...mergedUsers[existingIndex], ...pendingUser };
        });
        if (isActive) {
          setUsers(mergedUsers);
          setHasMore(page.length === PAGE_SIZE);
        }
      } catch (requestError) {
        if (isActive) {
          setError(requestError.response?.data?.message || "Unable to load users. Please try again.");
        }
      } finally {
        if (isActive) setLoading(false);
      }
    }

    load();
    return () => {
      isActive = false;
    };
  }, [token]);

  async function loadMore() {
    setLoadingMore(true);
    setError("");
    try {
      const response = await getUsers(token, PAGE_SIZE, users.length);
      const page = unwrapUsers(response.data);
      setUsers((existing) => [...existing, ...page]);
      setHasMore(page.length === PAGE_SIZE);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load more users.");
    } finally {
      setLoadingMore(false);
    }
  }

  function beginEdit(user) {
    setNotice("");
    setError("");
    setEditingId(userId(user));
    setEditValues({
      username: user.username || "",
      first_name: user.first_name ?? user.firstName ?? "",
      last_name: user.last_name ?? user.lastName ?? "",
    });
  }

  async function saveUser(event, user) {
    event.preventDefault();
    const id = userId(user);
    const values = {
      username: editValues.username.trim(),
      firstName: editValues.first_name.trim(),
      lastName: editValues.last_name.trim(),
    };
    if (!values.username || !values.firstName || !values.lastName) {
      setError("Username, first name, and last name are required.");
      return;
    }

    setSavingId(id);
    setError("");
    setNotice("");
    try {
      const response = await updateUser(token, id, values);
      const updated = unwrapProfile(response.data);
      setUsers((existing) => existing.map((item) =>
        sameUser(item, user)
          ? {
              ...item,
              username: values.username,
              first_name: values.firstName,
              last_name: values.lastName,
              ...(updated && typeof updated === "object" ? updated : {}),
            }
          : item,
      ));
      setEditingId(null);
      setNotice("User profile updated.");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to update this profile.");
    } finally {
      setSavingId(null);
    }
  }

  async function handleApprove(user) {
    const id = userId(user);
    setApprovingId(id);
    setError("");
    setNotice("");
    try {
      await approveUser(token, id);
      setUsers((existing) => existing.map((item) =>
        sameUser(item, user)
          ? { ...item, status: "approved", is_approved: true, isApproved: true, is_active: true, isActive: true }
          : item,
      ));
      setNotice(`${user.username || "User"} approved.`);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to approve this account.");
    } finally {
      setApprovingId(null);
    }
  }

  async function handleDeactivate(user) {
    const id = userId(user);
    if (!window.confirm(`Deactivate ${user.username || "this account"}? Their messages will be preserved.`)) return;

    setDeactivatingId(id);
    setError("");
    setNotice("");
    try {
      await deactivateUser(token, id);
      setUsers((existing) => existing.map((item) =>
        sameUser(item, user) ? { ...item, is_active: false, status: "inactive" } : item,
      ));
      setNotice("Account deactivated. Existing messages are preserved.");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to deactivate this account.");
    } finally {
      setDeactivatingId(null);
    }
  }

  if (loading) {
    return <main className="admin-users-page"><p className="admin-users-loading">Checking administrator access...</p></main>;
  }

  if (currentUser?.is_admin !== true) {
    return (
      <main className="admin-users-page">
        <section className="admin-users-denied" role="alert">
          <h1>Administrator access required</h1>
          <p>Your account cannot manage workspace users.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-users-page">
      <header className="admin-users-heading">
        <div>
          <p className="admin-users-eyebrow">WORKSPACE ADMIN</p>
          <h1>Users</h1>
          <p>Manage member profiles and account access.</p>
        </div>
        <span className="admin-user-count">{users.length} loaded</span>
      </header>

      {error && <p className="admin-users-alert" role="alert">{error}</p>}
      {notice && <p className="admin-users-notice" role="status">{notice}</p>}

      <div className="admin-users-table-wrap">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th scope="col">Username</th>
              <th scope="col">First name</th>
              <th scope="col">Last name</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const id = userId(user);
              const isEditing = editingId === id;
              const isInactive = userIsInactive(user);
              const isPending = userIsPending(user);
              const isCurrentUser = sameUser(currentUser, user);
              return (
                <tr key={id}>
                  {isEditing ? (
                    <>
                      {[
                        ["username", "Username"],
                        ["first_name", "First name"],
                        ["last_name", "Last name"],
                      ].map(([field, label]) => (
                        <td data-label={label} key={field}>
                          <label className="sr-only" htmlFor={`edit-${id}-${field}`}>{label} for {user.username}</label>
                          <input
                            id={`edit-${id}-${field}`}
                            onChange={(event) => setEditValues((values) => ({ ...values, [field]: event.target.value }))}
                            required
                            value={editValues[field]}
                          />
                        </td>
                      ))}
                    </>
                  ) : (
                    <>
                      <td data-label="Username">{user.username || "—"}</td>
                      <td data-label="First name">{user.first_name ?? user.firstName ?? "—"}</td>
                      <td data-label="Last name">{user.last_name ?? user.lastName ?? "—"}</td>
                    </>
                  )}
                  <td data-label="Role">{user.is_admin === true ? "Admin" : "Member"}</td>
                  <td data-label="Status">
                    <span className={`admin-user-status is-${userStatus(user).toLowerCase()}`}>
                      {userStatus(user)}
                    </span>
                  </td>
                  <td className="admin-user-actions" data-label="Actions">
                    {isEditing ? (
                      <>
                        <button className="admin-action-primary" disabled={savingId === id} onClick={(event) => saveUser(event, user)} type="button">
                          {savingId === id ? "Saving..." : "Save"}
                        </button>
                        <button className="admin-action-secondary" onClick={() => setEditingId(null)} type="button">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="admin-action-secondary" onClick={() => beginEdit(user)} type="button">Edit</button>
                        {isPending && (
                          <button
                            className="admin-action-approve"
                            disabled={approvingId === id}
                            onClick={() => handleApprove(user)}
                            type="button"
                          >
                            {approvingId === id ? "Approving..." : "Approve"}
                          </button>
                        )}
                        <button
                          className="admin-action-danger"
                          disabled={isInactive || isCurrentUser || deactivatingId === id}
                          onClick={() => handleDeactivate(user)}
                          type="button"
                        >
                          {deactivatingId === id ? "Deactivating..." : isInactive ? "Inactive" : "Make inactive"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!users.length && (
              <tr><td className="admin-users-empty" colSpan="6">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <button className="admin-load-more" disabled={loadingMore} onClick={loadMore} type="button">
          {loadingMore ? "Loading..." : "Load more users"}
        </button>
      )}
    </main>
  );
}